import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import "./NotificationBell.css";

/* ─────────────────────────────────────────────────────────────
   NOTIFICATION PANEL  (dropdown)
   ───────────────────────────────────────────────────────────── */
function NotificationPanel({ notifications, loading, onClose, onMarkRead, onDismiss, onMarkAllRead, onDismissAll, bellRef, onNotifClick }) {
  const panelRef  = useRef(null);
  const [pos, setPos] = useState(null);

  // Position the panel below the bell button
  useEffect(() => {
    const updatePos = () => {
      const bell = bellRef?.current;
      if (!bell) return;
      const rect = bell.getBoundingClientRect();
      setPos({
        top:   rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    updatePos();
    window.addEventListener("resize",  updatePos);
    window.addEventListener("scroll",  updatePos, true);
    return () => {
      window.removeEventListener("resize",  updatePos);
      window.removeEventListener("scroll",  updatePos, true);
    };
  }, [bellRef]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        bellRef?.current && !bellRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    const id = setTimeout(() => document.addEventListener("mousedown", handler), 10);
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", handler);
    };
  }, [onClose, bellRef]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return createPortal(
    <div ref={panelRef} className="notif-panel" style={pos ? { top: pos.top, right: pos.right } : { visibility: "hidden" }}>
      {/* Arrow pointer */}
      <div className="notif-panel__arrow" />

      <div className="notif-panel__header">
        <span className="notif-panel__title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          My Notifications
        </span>
        <div className="notif-panel__header-actions">
          {unreadCount > 0 && (
            <button className="notif-panel__text-btn" onClick={onMarkAllRead} title="Mark all as read">
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button className="notif-panel__text-btn notif-panel__text-btn--danger" onClick={onDismissAll} title="Clear all">
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="notif-panel__body">
        {loading ? (
          <div className="notif-panel__empty">
            <div className="notif-panel__spinner" />
            <p>Loading…</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notif-panel__empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>You're all caught up!</p>
            <span>No overdue assignments.</span>
          </div>
        ) : (
          <ul className="notif-panel__list">
            {notifications.map((item) => {
              const isCompleted = ["done", "completed", "closed", "resolved"].includes((item.state || "").toLowerCase());
              const targetDatePassed = item.targetDate ? new Date(item.targetDate).getTime() < new Date().setHours(0, 0, 0, 0) : false;

              // ETA Missed ONLY if targetDate is passed, flagged, or state contains overdue/missed
              const isEtaMissed = !isCompleted && (
                item.isEtaMissed === true ||
                item.isEtaMissed === 1 ||
                targetDatePassed ||
                (item.state || "").toLowerCase().includes("overdue") ||
                (item.state || "").toLowerCase().includes("missed")
              );

              let stateBg = "#eff6ff";    // Soft Blue background for Creation
              let stateColor = "#1d4ed8"; // Royal Blue text for Creation
              let badgeBorder = "#bfdbfe";

              if (isEtaMissed) {
                stateBg = "#ffffff";
                stateColor = "#dc2626";
                badgeBorder = "#fca5a5";
              } else if (isCompleted) {
                stateBg = "#f0fdf4";
                stateColor = "#15803d";
                badgeBorder = "#bbf7d0";
              }

              return (
                <li
                  key={item.notificationId || item.id}
                  className={`notif-panel__item${!item.isRead ? " notif-panel__item--unread" : ""}`}
                  style={{
                    background: isEtaMissed ? "#fff1f1" : !item.isRead ? "#fff8f8" : "#ffffff",
                    border: isEtaMissed ? "1px solid #fecaca" : !item.isRead ? "1px solid #fee2e2" : "1px solid #e2e8f0",
                    borderRadius: "14px",
                    padding: "12px 14px",
                  }}
                  onClick={() => onNotifClick(item)}
                >
                  <div className="notif-panel__item-top">
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ fontSize: "14px", fontWeight: "800", color: isEtaMissed ? "#dc2626" : "#2563eb" }}>
                        {item.id}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      {!item.isRead && (
                        <span
                          className="notif-panel__item-state"
                          style={{
                            backgroundColor: stateBg,
                            color: stateColor,
                            border: `1px solid ${badgeBorder}`,
                            borderRadius: "16px",
                            padding: "2px 10px",
                            fontWeight: "700",
                            fontSize: "11px"
                          }}
                        >
                          {isEtaMissed ? "ETA Missed" : (item.state || "New")}
                        </span>
                      )}
                      <button
                        className="notif-panel__dismiss-btn"
                        onClick={(e) => { e.stopPropagation(); onDismiss(item.notificationId); }}
                        title="Dismiss"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="notif-panel__item-title" title={item.title} style={{ fontSize: "13.5px", fontWeight: "700", color: "#0f172a", marginTop: "4px", marginBottom: "4px", lineHeight: 1.3 }}>
                    {item.title && item.title.length > 32
                      ? item.title.substring(0, 30) + "..."
                      : item.title}
                  </div>
                  <div className="notif-panel__item-meta" style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11.5px", color: "#64748b" }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: "13px", height: "13px" }}>
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {item.targetDate
                      ? new Date(item.targetDate).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                      : "No ETA set"}
                    {item.triggeredBy && (
                      <span className="notif-panel__triggered-by"> · by {item.triggeredBy.split("@")[0]}</span>
                    )}
                  </div>
                </li>
              );
            })}
        </ul>
        )}
      </div>

      <div className="notif-panel__footer">
        <button className="notif-panel__close-btn" onClick={onClose}>Close</button>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────────────────────
   NOTIFICATION BELL  (icon button in topbar)
   ───────────────────────────────────────────────────────────── */
export default function NotificationBell() {
  const { instance, accounts } = useMsal();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [open, setOpen]                   = useState(false);
  const bellRef                           = useRef(null);

  const email = accounts?.[0]?.username || "";

  /* ── Fetch full notification list from DB ── */
  const fetchNotifications = useCallback(async () => {
    if (!email) return;
    try {
      setLoading(true);
      const res  = await callApi(instance, accounts, "GET", "/api/assignments/notifications");
      const data = res?.data?.data || [];
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("NotificationBell: failed to load notifications", err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, email]);


  // Load on mount only — no polling. Re-fetch happens on each panel open.
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refresh badge immediately when the ETA Missed Alert job finishes
  useEffect(() => {
    const handler = () => fetchNotifications();
    window.addEventListener("eta-alert-triggered", handler);
    return () => window.removeEventListener("eta-alert-triggered", handler);
  }, [fetchNotifications]);


  /* ── Actions ── */
  const markRead = useCallback(async (notifId) => {
    try {
      await callApi(instance, accounts, "PATCH", `/api/assignments/notifications/${notifId}/read`);
      setNotifications(prev =>
        prev.map(n => n.notificationId === notifId ? { ...n, isRead: true } : n)
      );
    } catch (e) { console.error("markRead failed", e); }
  }, [instance, accounts]);

  const markAllRead = useCallback(async () => {
    try {
      await callApi(instance, accounts, "PATCH", "/api/assignments/notifications/read-all");
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (e) { console.error("markAllRead failed", e); }
  }, [instance, accounts]);

  const dismiss = useCallback(async (notifId) => {
    try {
      await callApi(instance, accounts, "DELETE", `/api/assignments/notifications/${notifId}`);
      setNotifications(prev => prev.filter(n => n.notificationId !== notifId));
    } catch (e) { console.error("dismiss failed", e); }
  }, [instance, accounts]);

  const dismissAll = useCallback(async () => {
    try {
      await callApi(instance, accounts, "DELETE", "/api/assignments/notifications");
      setNotifications([]);
    } catch (e) { console.error("dismissAll failed", e); }
  }, [instance, accounts]);

  const toggle = () => {
    if (!open) fetchNotifications(); // refresh on open
    setOpen(prev => !prev);
  };
  const navigate = useNavigate();
  const close = useCallback(() => setOpen(false), []);

  const handleNotifClick = useCallback((item) => {
    if (!item.isRead) {
      markRead(item.notificationId);
    }
    close();
    const org = item.organization || "";
    const proj = item.project || "";
    navigate(`/request-pool?view=detail&id=${item.id}&org=${encodeURIComponent(org)}&proj=${encodeURIComponent(proj)}`);
  }, [markRead, close, navigate]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <>
      <button
        ref={bellRef}
        type="button"
        className={`notif-bell${unreadCount > 0 ? " notif-bell--active" : ""}`}
        onClick={toggle}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
        title={unreadCount > 0 ? `${unreadCount} unread` : "No new notifications"}
        style={{ position: "relative" }}
      >
        <svg className="notif-bell__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 && (
          <span className="notif-bell__badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <NotificationPanel
          notifications={notifications}
          loading={loading}
          onClose={close}
          onMarkRead={markRead}
          onDismiss={dismiss}
          onMarkAllRead={markAllRead}
          onDismissAll={dismissAll}
          bellRef={bellRef}
          onNotifClick={handleNotifClick}
        />
      )}
    </>
  );
}
